import axios from "axios";
import { getCsrfToken } from "../utils/csrf";

// Base URL for all allauth endpoints
const ALLAUTH_BASE_URL = "/_allauth/browser/v1";

const formatEndpoint = (endpoint: string): string => {
  const noLeadingSlash = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
  return noLeadingSlash.endsWith('/') ? noLeadingSlash.slice(0, -1) : noLeadingSlash;
};

const api = axios.create({
  baseURL: ALLAUTH_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

export interface UserData {
  id?: number;
  email: string;
  first_name?: string;
  last_name?: string;
  display?: string;
  has_usable_password?: boolean;
}

interface LoginResponse {
  user: UserData;
}

interface SignupData {
  email: string;
  password1: string;
  password2: string;
  [key: string]: any;
}

export interface AuthError {
  message: string;
  field_errors?: Record<string, string[]>;
  non_field_errors?: string[];
  detail?: string;
  code?: string;
  data?: any;
}

interface AuthResult<T = any> {
  success: boolean;
  data?: T;
  error?: AuthError;
}

interface SessionResponse {
  user: UserData | null;
}

interface SocialAccount {
  id: string;
  provider: string;
  user: {
    email: string;
  };
}

interface Provider {
  id: string;
  name: string;
}

interface SocialAuthResponse {
  url: string;
}


api.interceptors.request.use(async (config) => {
  if (config.method !== "get") {
    const csrfToken = await getCsrfToken();
    if (csrfToken) {
      config.headers["X-CSRFToken"] = csrfToken;
    }
  }
  return config;
});

/**
 * Auth service for integration with django-allauth headless API
 */
const authService = {
  _sessionCache: {
    data: null as SessionResponse | null,
    timestamp: 0,
    TTL: 5000, // 5 seconds
  },

  _isEmailVerificationError(error: any): boolean {
    if (!error) return false;

    const errorMsg = error.message || '';
    const errorDetail = error.detail || '';
    const nonFieldErrors = error.non_field_errors || [];

    if (error.data?.flows) {
      const flows = error.data.flows;
      const verifyEmailFlow = flows.find((flow: any) => flow.id === 'verify_email');
      if (verifyEmailFlow && verifyEmailFlow.is_pending === true) {
        return true;
      }
    }

    return (
      errorMsg.toLowerCase().includes('email verification') ||
      errorDetail.toLowerCase().includes('email verification') ||
      errorMsg.toLowerCase().includes('verify') ||
      errorDetail.toLowerCase().includes('verify') ||
      (error.code === 'account_email_verification_required') ||
      nonFieldErrors.some((err: string) =>
        err.toLowerCase().includes('verification') ||
        err.toLowerCase().includes('verify')
      )
    );
  },

  /**
   * Check current session status
   * @returns Session data with user object if authenticated
   */
  async getSession(): Promise<SessionResponse> {
    const now = Date.now();

    if (this._sessionCache.data &&
        now - this._sessionCache.timestamp < this._sessionCache.TTL) {
      return this._sessionCache.data;
    }

    try {
      const sessionEndpoint = `${ALLAUTH_BASE_URL}/${formatEndpoint("auth/session")}`;

      const response = await fetch(sessionEndpoint, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        }
      });

      if (!response.ok) {
        return { user: null };
      }

      const data = await response.json();

      const sessionData = data.data ? { user: data.data.user } : { user: null };

      this._sessionCache.data = sessionData;
      this._sessionCache.timestamp = now;

      return sessionData;
    } catch (error) {
      return { user: null };
    }
  },

  /**
   * Login user with email and password
   * @param email User email
   * @param password User password
   * @returns Result with user data or error
   */
  login: async (email: string, password: string): Promise<AuthResult<LoginResponse>> => {
    try {
      const csrfToken = await getCsrfToken();

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      };

      const loginResponse = await fetch(`${ALLAUTH_BASE_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {})
        },
        body: JSON.stringify({ email, password })
      });

      if (!loginResponse.ok) {
        throw new Error(`Login failed: ${loginResponse.status}`);
      }

      const data = await loginResponse.json();
      const hasSessionCookie = document.cookie
        .split(';')
        .some(item => item.trim().toLowerCase().startsWith('sessionid='));

      return {
        success: true,
        data: data.data || data
      };
    } catch (error: any) {
      if (error.response?.status === 403 &&
          (error.response?.data?.detail?.includes('CSRF') ||
           (typeof error.response?.data === 'string' &&
            error.response?.data.includes('CSRF')))) {
        try {
          const newToken = await fetch(`${ALLAUTH_BASE_URL}/config`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          }).then(r => r.json()).then(data => data.csrf_token);

          if (newToken) {
            const loginResponse = await fetch(`${ALLAUTH_BASE_URL}/auth/login`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRFToken': newToken
              },
              body: JSON.stringify({ email, password })
            });

            if (!loginResponse.ok) {
              throw new Error(`Login retry failed: ${loginResponse.status}`);
            }

            const loginData = await loginResponse.json();
            return {
              success: true,
              data: loginData.data || loginData
            };
          }
        } catch (retryError) {
        }
      }

      const responseData = error.response?.data || { message: "Login failed" };

      if ((error.response?.status === 401 || error.response?.status === 403) &&
          authService._isEmailVerificationError(responseData)) {
        return {
          success: false,
          error: {
            message: 'Email verification required',
            code: 'account_email_verification_required',
            detail: 'Please check your email inbox for a verification link.',
            data: responseData
          }
        };
      }

      return {
        success: false,
        error: responseData,
      };
    }
  },

  /**
   * Register new user
   * @param userData User registration data
   * @returns Result with success status or error
   */
  register: async (userData: SignupData): Promise<AuthResult> => {
    try {
      const response = await api.post("/auth/signup", userData);
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Registration failed" },
      };
    }
  },

  /**
   * Logout current user
   * @returns Result with success status
   */
  logout: async (): Promise<AuthResult> => {
    try {
      await api.delete("/auth/session");
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Logout failed" },
      };
    }
  },

  /**
   * Request password reset for email
   * @param email User email
   * @returns Result with success status or error
   */
  requestPasswordReset: async (email: string): Promise<AuthResult> => {
    try {
      await api.post("/auth/password/request", { email });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Password reset request failed" },
      };
    }
  },

  /**
   * Reset password with key from email link
   * @param key Reset key from email
   * @param password1 New password
   * @param password2 New password confirmation
   * @returns Result with success status or error
   */
  resetPassword: async (
    key: string,
    password1: string,
    password2: string
  ): Promise<AuthResult> => {
    try {
      await api.post("/auth/password/reset", { key, password1, password2 });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Password reset failed" },
      };
    }
  },

  /**
   * Verify email with key from verification email
   * @param key Verification key from email
   * @returns Result with success status or error
   */
  verifyEmail: async (key: string): Promise<AuthResult> => {
    try {
      let response = null;

      try {
        response = await api.post("/auth/email/verify", { key });
      } catch (postError: any) {
        const csrfToken = await getCsrfToken();

        const fetchResponse = await fetch(`${ALLAUTH_BASE_URL}/auth/email/verify`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRFToken': csrfToken || ''
          },
          body: JSON.stringify({ key })
        });

        if (!fetchResponse.ok) {
          throw postError; // If fetch also failed, reuse the original error
        }

        response = { data: await fetchResponse.json() };
      }

      return {
        success: true,
        data: response?.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Email verification failed" },
      };
    }
  },

  /**
   * Change password for authenticated user
   * @param oldPassword Current password
   * @param password1 New password
   * @param password2 New password confirmation
   * @returns Result with success status or error
   */
  changePassword: async (
    oldPassword: string,
    password1: string,
    password2: string
  ): Promise<AuthResult> => {
    try {
      await api.post("/account/password/change", {
        oldPassword,
        password1,
        password2,
      });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Password change failed" },
      };
    }
  },

  /**
   * Manage user email addresses (add/remove/set primary)
   * @returns Current email addresses
   */
  getEmails: async (): Promise<AuthResult<any>> => {
    try {
      const { data } = await api.get("/account/email");
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Failed to get emails" },
      };
    }
  },

  /**
   * Add a new email address
   * @param email New email address
   * @returns Result with success status or error
   */
  addEmail: async (email: string): Promise<AuthResult> => {
    try {
      await api.post("/account/email", { email });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Failed to add email" },
      };
    }
  },

  /**
   * Remove an email address
   * @param email Email address to remove
   * @returns Result with success status or error
   */
  removeEmail: async (email: string): Promise<AuthResult> => {
    try {
      await api.delete("/account/email", { data: { email } });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Failed to remove email" },
      };
    }
  },

  /**
   * Set email as primary
   * @param email Email address to set as primary
   * @returns Result with success status or error
   */
  setPrimaryEmail: async (email: string): Promise<AuthResult> => {
    try {
      await api.post("/account/email", { email, primary: true });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Failed to set primary email" },
      };
    }
  },

  /**
   * Get list of available social providers
   * @returns List of available providers
   */
  getAvailableProviders: async (): Promise<AuthResult<any>> => {
    // Return a placeholder response until implementation is completed
    return {
      success: false,
      error: {
        message: "This functionality is not yet implemented"
      }
    };
  },

  /**
   * Start OAuth process with a provider
   * @param providerId ID of the provider (e.g. "google")
   * @returns URL to redirect to for OAuth
   */
  startProviderAuth: async (providerId: string): Promise<AuthResult<string>> => {
    try {
      const response = await api.post("/socialaccount/providers/connect", {
        provider: providerId
      });
      return {
        success: true,
        data: response.data.data.url
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Failed to start provider auth" }
      };
    }
  },

  /**
   * Resend email verification
   * @param email Email address to resend verification for
   * @returns Result with success status or error
   */
  resendVerification: async (email: string): Promise<AuthResult> => {
    try {
      const csrfToken = await getCsrfToken();
      const response = await axios.put(
        `${ALLAUTH_BASE_URL}/browser/v1/account/email`,
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRFToken': csrfToken || ''
          },
          withCredentials: true
        }
      );

      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Failed to resend verification email" },
      };
    }
  },

  /**
   * Get email verification data using key from verification link
   * This is an optional step that might be needed before verifying the email
   * following django-allauth's pattern
   *
   * @param key Verification key from email
   * @returns Result with verification data or error
   */
  getEmailVerificationData: async (key: string): Promise<AuthResult<any>> => {
    try {
      const csrfToken = await getCsrfToken();

      const headers: Record<string, string> = {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Email-Verification-Key': key
      };

      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await axios.get(`${ALLAUTH_BASE_URL}/auth/email/verify`, {
        headers,
        withCredentials: true
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Failed to get email verification data" },
      };
    }
  },

  /**
   * Get list of connected social accounts
   * @returns List of connected accounts
   */
  getSocialAccounts: async (): Promise<AuthResult<SocialAccount[]>> => {
    try {
      const response = await api.get("/account/providers");
      return {
        success: true,
        data: response.data.accounts || []
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Failed to get social accounts" }
      };
    }
  },

  /**
   * Disconnect a social account
   * @param accountId ID of the account to disconnect
   * @returns Success status
   */
  disconnectSocialAccount: async (accountId: string): Promise<AuthResult> => {
    try {
      await api.post("/account/providers/disconnect", {
        account: accountId
      });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Failed to disconnect account" }
      };
    }
  },

  /**
   * Start OAuth flow with a provider
   * @param providerId ID of the provider (e.g. "google")
   * @param process Type of process ("login" or "connect")
   * @returns URL to redirect to
   */
  startOAuthFlow: async (providerId: string, process: "login" | "connect"): Promise<AuthResult<string>> => {
    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${ALLAUTH_BASE_URL}/auth/provider/redirect`;

      const providerInput = document.createElement('input');
      providerInput.type = 'hidden';
      providerInput.name = 'provider';
      providerInput.value = providerId;
      form.appendChild(providerInput);

      const processInput = document.createElement('input');
      processInput.type = 'hidden';
      processInput.name = 'process';
      processInput.value = process;
      form.appendChild(processInput);

      const callbackInput = document.createElement('input');
      callbackInput.type = 'hidden';
      callbackInput.name = 'callback_url';
      callbackInput.value = `${window.location.origin}/accounts/google/login/callback/`;
      form.appendChild(callbackInput);

      const csrfToken = await getCsrfToken();
      if (csrfToken) {
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'csrfmiddlewaretoken';
        csrfInput.value = csrfToken;
        form.appendChild(csrfInput);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || { message: "Failed to start OAuth flow" }
      };
    }
  },
};

export default authService;

